'use client';

import { c as _c } from "react/compiler-runtime";
import { Divider } from '@algoacademy/design-system/web/layouts/Divider';
import type { MentoringDiscountDraft } from '../../_components/DiscountControls';
import { BuyerDataSection } from './_components/BuyerDataSection';
import { DiscountPricingSection } from './_components/DiscountPricingSection';
import { FormActions } from './_components/FormActions';
import { FormFeedback } from './_components/FormFeedback';
import { OfferFormHeader } from './_components/OfferFormHeader';
import { StudentLookupSection } from './_components/StudentLookupSection';
import type { MentoringOfferProduct } from './_helpers/mentoring-terms';
import { useMentoringOfferForm } from './_hooks/useMentoringOfferForm';
type MentoringOfferFormProps = {
  mentoringProduct: MentoringOfferProduct;
  /**
   * The discount being priced. It belongs to the card above, which shows the
   * price ladder it makes, so this form only reads it and hands back every edit.
   */
  discountDraft: MentoringDiscountDraft;
  onDiscountChange: (nextDraft: MentoringDiscountDraft) => void;
};

/**
 * There is one staff form, and it lives on every page carrying the pricing
 * card above it - `/mentoring`, `/cennik`, the home page, the goal page.
 * Whatever the visitor configured above - mentoring type, cadence, hours -
 * either becomes a new offer with a `/mentoring/<id>` link for a student who
 * has no subscription, or the new terms of the subscription a student already
 * has. Which one it is comes from looking the typed student e-mail up. The
 * form always states a price - a discount, or list price on purpose - and
 * nobody without the payments permission sees any of it.
 */
export function MentoringOfferForm(t0) {
  const $ = _c(50);
  const {
    mentoringProduct,
    discountDraft,
    onDiscountChange
  } = t0;
  let t1;
  if ($[0] !== discountDraft || $[1] !== mentoringProduct || $[2] !== onDiscountChange) {
    t1 = {
      mentoringProduct,
      discountDraft,
      onDiscountChange
    };
    $[0] = discountDraft;
    $[1] = mentoringProduct;
    $[2] = onDiscountChange;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const ctx = useMentoringOfferForm(t1);
  if (!ctx.hasPaymentsPermission) {
    return null;
  }
  let t2;
  if ($[4] !== ctx.chosenTermsSummary || $[5] !== ctx.isChangeMode) {
    t2 = <OfferFormHeader isChangeMode={ctx.isChangeMode} chosenTermsSummary={ctx.chosenTermsSummary} />;
    $[4] = ctx.chosenTermsSummary;
    $[5] = ctx.isChangeMode;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== ctx.form || $[8] !== ctx.onSubmit) {
    t3 = ctx.form.handleSubmit(ctx.onSubmit);
    $[7] = ctx.form;
    $[8] = ctx.onSubmit;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== ctx.current || $[11] !== ctx.form || $[12] !== ctx.isBlocked || $[13] !== ctx.isChangeMode || $[14] !== ctx.isLookupPending || $[15] !== ctx.isStudentEmailValid || $[16] !== ctx.mentoringState) {
    t4 = <StudentLookupSection form={ctx.form} isChangeMode={ctx.isChangeMode} isBlocked={ctx.isBlocked} isStudentEmailValid={ctx.isStudentEmailValid} isLookupPending={ctx.isLookupPending} mentoringState={ctx.mentoringState} current={ctx.current} />;
    $[10] = ctx.current;
    $[11] = ctx.form;
    $[12] = ctx.isBlocked;
    $[13] = ctx.isChangeMode;
    $[14] = ctx.isLookupPending;
    $[15] = ctx.isStudentEmailValid;
    $[16] = ctx.mentoringState;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== ctx.current || $[19] !== ctx.discountDraft || $[20] !== ctx.discountInForceDescription || $[21] !== ctx.form || $[22] !== ctx.isBlocked || $[23] !== ctx.isChangeMode || $[24] !== ctx.mentoringProduct || $[25] !== ctx.onDiscountChange) {
    t5 = !ctx.isBlocked ? <><Divider /><BuyerDataSection form={ctx.form} isChangeMode={ctx.isChangeMode} current={ctx.current} /><Divider /><DiscountPricingSection mentoringProduct={ctx.mentoringProduct} discountDraft={ctx.discountDraft} onDiscountChange={ctx.onDiscountChange} discountInForceDescription={ctx.discountInForceDescription} /></> : null;
    $[18] = ctx.current;
    $[19] = ctx.discountDraft;
    $[20] = ctx.discountInForceDescription;
    $[21] = ctx.form;
    $[22] = ctx.isBlocked;
    $[23] = ctx.isChangeMode;
    $[24] = ctx.mentoringProduct;
    $[25] = ctx.onDiscountChange;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Divider />;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== ctx) {
    t7 = () => ctx.setIsConfirmOpen(true);
    $[28] = ctx;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== ctx.current || $[31] !== ctx.isBlocked || $[32] !== ctx.isChangeMode || $[33] !== ctx.isChangePending || $[34] !== ctx.isLookupPending || $[35] !== ctx.isUpsertPending || $[36] !== ctx.mentoringState || $[37] !== t7) {
    t8 = <FormActions isChangeMode={ctx.isChangeMode} isBlocked={ctx.isBlocked} isLookupPending={ctx.isLookupPending} isUpsertPending={ctx.isUpsertPending} isChangePending={ctx.isChangePending} mentoringState={ctx.mentoringState} current={ctx.current} onOpenConfirm={t7} />;
    $[30] = ctx.current;
    $[31] = ctx.isBlocked;
    $[32] = ctx.isChangeMode;
    $[33] = ctx.isChangePending;
    $[34] = ctx.isLookupPending;
    $[35] = ctx.isUpsertPending;
    $[36] = ctx.mentoringState;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== t3 || $[40] !== t4 || $[41] !== t5 || $[42] !== t8) {
    t9 = <form onSubmit={t3} className="flex flex-col gap-6">{t4}{t5}{t6}{t8}</form>;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t8;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== ctx.feedback) {
    t10 = <FormFeedback model={ctx.feedback} />;
    $[44] = ctx.feedback;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  let t11;
  if ($[46] !== t10 || $[47] !== t2 || $[48] !== t9) {
    t11 = <div className="flex w-full flex-col gap-6">{t2}{t9}{t10}</div>;
    $[46] = t10;
    $[47] = t2;
    $[48] = t9;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  return t11;
}